import { Badge, type ContextMenuItem, IconTile } from "@design";
import { Lock, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SpaceResourceResponse } from "@/api";
import { EntityRow } from "@/components/build/EntityRow";
import { nameOf } from "./useSpaceItems";

/** An agent or data source in the space, with its visibility and what I can do with it. */
export function SpaceItemRow({
  item,
  icon,
  onOpen,
  onShare,
  onMakePrivate,
  onGiveAccess,
  onRemoveAccess,
  onTogglePin,
  onRemove,
}: Readonly<{
  item: SpaceResourceResponse;
  icon: ReactNode;
  onOpen: () => void;
  onShare: () => void;
  onMakePrivate: () => void;
  onGiveAccess: () => void;
  onRemoveAccess: () => void;
  onTogglePin: () => void;
  onRemove: () => void;
}>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.items" });
  const shared = item.visibility === "space";
  const menu: ContextMenuItem[] = [];
  if (!shared && item.isMine) menu.push({ label: t("share"), onSelect: onShare });
  if (shared && item.canGrant && !item.spaceHasAccess) menu.push({ label: t("giveAccess"), onSelect: onGiveAccess });
  if (item.canRevokeAccess) menu.push({ label: t("removeAccess"), onSelect: onRemoveAccess });
  if (shared && item.canChange) menu.push({ label: t("makePrivate"), onSelect: onMakePrivate });
  menu.push({ label: item.isPinned ? t("unpin") : t("pin"), onSelect: onTogglePin });
  if (item.canChange) menu.push({ label: t("remove"), danger: true, onSelect: onRemove });

  const details = [
    item.addedByName && t("addedBy", { name: item.addedByName }),
    item.spaceHasAccess && t("spaceHasAccess"),
    !item.canUse && t("noAccess"),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <EntityRow
      icon={<IconTile>{icon}</IconTile>}
      name={nameOf(item)}
      description={details || null}
      meta={
        <Badge variant={shared ? "info" : "neutral"} icon={shared ? <Users size={12} /> : <Lock size={12} />}>
          {shared ? t("shared") : t("onlyYou")}
        </Badge>
      }
      onOpen={onOpen}
      menuItems={menu}
    />
  );
}
